/**
 * Tone.js implementation of the AudioPlayer interface.
 *
 * Uses Tone.PolySynth for the synth fallback (always available, instant) and
 * Tone.Sampler for sample-based instruments (piano, guitar, voice).
 *
 * Design notes:
 * - Synth is created in init() — no async wait needed.
 * - Samplers are created lazily on first loadInstrument(name) call.
 * - The active instrument can be switched freely once loaded; switching to an
 *   unloaded instrument throws.
 * - playPlan() schedules every NoteEvent against Tone's transport, so timing
 *   is sample-accurate within Tone's tolerances (typically <10ms drift).
 *
 * See spec section 4.2.
 */

import * as Tone from "tone";
import type { AudioPlan, NoteEvent } from "@music-app/exercise-generator";
import { foldEventsForInstrument } from "@music-app/exercise-generator";
import type { AudioPlayer, AudioPlayerOptions, AudioPlayerState, Instrument } from "./types.js";
import { midiToNoteName, gainToVelocity } from "./midi.js";
import { SAMPLE_SOURCES } from "./sample-sources.js";
import { LatencyMonitor } from "./latency.js";

type Voice = Tone.PolySynth | Tone.Sampler;

/** Scheduling lead before the first event of a play. The audio output device
 *  spins up on the first sound after a context resume, clipping that sound's
 *  onset; the first play warms the graph and uses the longer lead so the
 *  audible cadence lands on an already-running device. Warm plays stay snappy. */
const WARM_START_LEAD = 0.05;
const COLD_START_LEAD = 0.4;
/**
 * How long (ms) after the last audible note the output device stays warm.
 * Hardware typically enters a low-power state after ~200–400 ms of silence.
 * We're conservative here: if more than this has passed since the plan ended,
 * treat the next play as a cold start and re-fire the warm-up note.
 */
const DEVICE_WARMDOWN_MS = 400;

export class TonePlayer implements AudioPlayer {
  private _state: AudioPlayerState = "uninitialized";
  private currentInstrument: Instrument = "synth";
  private voices: Partial<Record<Instrument, Voice>> = {};
  private masterVolume: Tone.Volume | null = null;
  private currentPlaybackController: AbortController | null = null;
  private scheduledTimeouts: number[] = [];
  private latencyMonitor: LatencyMonitor | null = null;
  /**
   * Absolute timestamp (performance.now()) until which the output device is
   * considered warm. Set to `now + plan_duration + DEVICE_WARMDOWN_MS` after
   * each play; reset to 0 on context suspend or destroy. When expired, the
   * next play fires a silent warm-up note and uses the longer COLD_START_LEAD.
   */
  private primedUntilMs = 0;

  get state(): AudioPlayerState {
    return this._state;
  }

  async init(): Promise<void> {
    if (this._state !== "uninitialized") {
      // Already set up — but the context may have been suspended since. Resume
      // it so a re-init() before playback (the drill calls init() each round)
      // still unlocks audio.
      await this.ensureRunning();
      return;
    }

    // Tone.start() resumes the AudioContext. Must be called from a user gesture.
    await Tone.start();

    this.masterVolume = new Tone.Volume(0).toDestination();

    // The synth voice is always available with no loading.
    const synth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: "triangle" },
      envelope: { attack: 0.01, decay: 0.15, sustain: 0.4, release: 0.6 },
    }).connect(this.masterVolume);
    this.voices.synth = synth;

    if (typeof window !== "undefined" && (window as unknown as { __E2E__?: boolean }).__E2E__) {
      this.attachE2EAudioTap();
    }

    this._state = "idle";
  }

  /**
   * Resume the AudioContext if the browser/OS has suspended it. Browsers
   * suspend a running context between user gestures (tab backgrounded, audio
   * focus lost, power saving), and `init()` runs `Tone.start()` only once —
   * so without this a later `play()` schedules notes into a suspended context
   * and produces silence. This is the intermittent "sometimes no sound" bug.
   * Safe to call repeatedly: it no-ops when already running. play()/playNote()
   * are always reached from a user-gesture call stack, so the resume is allowed.
   */
  private async ensureRunning(): Promise<void> {
    if (this._state === "uninitialized") return;
    if (Tone.getContext().state !== "running") {
      await Tone.start();
      // Cold context — treat the next play as a cold start so the warm-up
      // note re-fires and the longer COLD_START_LEAD is used.
      this.primedUntilMs = 0;
    }
  }

  /**
   * Test-only: tap the master output with an AnalyserNode and expose
   * `window.__audioTap__` for Playwright to read peak amplitude. Gated on
   * `window.__E2E__` so production builds never run this code path.
   */
  private attachE2EAudioTap(): void {
    if (!this.masterVolume) return;
    const analyser = Tone.getContext().createAnalyser();
    analyser.fftSize = 1024;
    this.masterVolume.connect(analyser);
    const buffer = new Float32Array(analyser.fftSize);
    let peakSinceArm = 0;

    const samplePeak = (): number => {
      analyser.getFloatTimeDomainData(buffer);
      let p = 0;
      for (let i = 0; i < buffer.length; i++) {
        const a = Math.abs(buffer[i]!);
        if (a > p) p = a;
      }
      if (p > peakSinceArm) peakSinceArm = p;
      return p;
    };

    (window as unknown as { __audioTap__: unknown }).__audioTap__ = {
      sample: samplePeak,
      peakSinceArm: () => {
        samplePeak();
        return peakSinceArm;
      },
      arm: () => {
        peakSinceArm = 0;
      },
    };
  }

  async loadInstrument(
    name: Instrument,
    opts?: { onProgress?: (loaded: number, total: number) => void },
  ): Promise<void> {
    if (this._state === "uninitialized") {
      throw new Error("AudioPlayer not initialized — call init() first");
    }
    if (name === "synth" || this.voices[name]) return; // already loaded

    const source = SAMPLE_SOURCES[name];
    if (!source || Object.keys(source.urls).length === 0) {
      if (name === "guitar") {
        // Plucked-string approximation: sawtooth (rich harmonics) with instant
        // attack and full decay to zero — no sustain, like a real guitar string.
        this.voices.guitar = new Tone.PolySynth(Tone.Synth, {
          oscillator: { type: "sawtooth" as const },
          envelope: { attack: 0.002, decay: 1.0, sustain: 0.0, release: 0.5 },
        }).connect(this.masterVolume!);
        return;
      }
      // eslint-disable-next-line no-console
      console.warn(`[audio-engine-web] No samples available for "${name}", will use synth`);
      return;
    }

    this._state = "loading";

    // Tone.Sampler takes a single onload callback fired after the whole
    // manifest finishes — no per-file granularity. Pre-loading each URL into
    // a ToneAudioBuffer lets us count completions and report real progress;
    // the Sampler is then constructed from the pre-loaded buffers.
    const entries = Object.entries(source.urls);
    const total = entries.length;
    let loaded = 0;
    opts?.onProgress?.(0, total);

    try {
      const pairs = await Promise.all(
        entries.map(async ([note, relativeUrl]) => {
          const buffer = await new Tone.ToneAudioBuffer().load(source.baseUrl + relativeUrl);
          loaded += 1;
          opts?.onProgress?.(loaded, total);
          return [note, buffer] as const;
        }),
      );
      const buffers: Record<string, Tone.ToneAudioBuffer> = Object.fromEntries(pairs);
      const sampler = new Tone.Sampler({
        urls: buffers,
        release: source.release,
      }).connect(this.masterVolume!);
      this.voices[name] = sampler;
      this._state = "idle";
    } catch (err) {
      this._state = "idle";
      throw err;
    }
  }

  setInstrument(name: Instrument): void {
    if (!this.isInstrumentLoaded(name)) {
      throw new Error(`Instrument "${name}" not loaded — call loadInstrument() first`);
    }
    this.currentInstrument = name;
  }

  getInstrument(): Instrument {
    return this.currentInstrument;
  }

  isInstrumentLoaded(name: Instrument): boolean {
    if (name === "synth") return this.voices.synth !== undefined;
    return this.voices[name] !== undefined;
  }

  async play(plan: AudioPlan, opts: AudioPlayerOptions = {}): Promise<void> {
    if (this._state === "uninitialized") {
      throw new Error("AudioPlayer not initialized");
    }

    // Resume first — the context may have been suspended since init().
    await this.ensureRunning();

    // Fall back to synth if the requested instrument's samples weren't loaded.
    const voice = this.voices[this.currentInstrument] ?? this.voices.synth!;

    // Fold notes outside the instrument's playable range back into it, so a
    // guitar never pitch-shifts a sample above D6 (its physical ceiling).
    const events = foldEventsForInstrument(plan.events, this.currentInstrument);

    if (opts.masterVolume !== undefined && this.masterVolume) {
      this.masterVolume.volume.value = Tone.gainToDb(opts.masterVolume);
    }

    this.stop();
    this.currentPlaybackController = new AbortController();
    const signal = this.currentPlaybackController.signal;

    this._state = "playing";

    const now = Tone.now();
    const warm = performance.now() < this.primedUntilMs;
    const lead = warm ? WARM_START_LEAD : COLD_START_LEAD;
    if (!warm) {
      // Warm-up pulse: activates the OS audio DAC before the first audible note.
      // Must use the synth (oscillator-based), not the sampler — a sampler at
      // near-zero velocity produces PCM ≈ 0 which the OS treats as silence and
      // leaves the DAC asleep. The synth always produces a real waveform.
      // Gain 0.005 (−46 dB) is inaudible but above the hardware zero threshold.
      // Scheduled 10 ms out to guarantee it lands in a future audio buffer.
      const synth = this.voices.synth;
      if (synth) synth.triggerAttackRelease(midiToNoteName(48), 0.08, now + 0.01, 0.005);
    }
    // Extend the warm window to cover this plan's duration plus a cooldown
    // buffer. The device stays active while audio is playing; DEVICE_WARMDOWN_MS
    // is how long it takes to sleep after the last audible note ends.
    this.primedUntilMs = performance.now() + (lead + plan.totalDuration + DEVICE_WARMDOWN_MS / 1000) * 1000;
    const startTime = now + lead;

    this.latencyMonitor = new LatencyMonitor({
      onMeasure: opts.onLatencyMeasured ? (ms) => opts.onLatencyMeasured!(ms) : undefined,
    });
    if (events.length > 0) {
      const firstOffset = events.reduce(
        (min, e) => (e.startTime < min ? e.startTime : min),
        events[0]!.startTime,
      );
      this.latencyMonitor.armPlay(performance.now(), firstOffset);
    }

    for (const event of events) {
      this.scheduleEvent(voice, event, startTime, opts);
    }

    return new Promise<void>((resolve) => {
      const totalMs = (plan.totalDuration + 0.1) * 1000;
      const timeoutId = window.setTimeout(() => {
        if (signal.aborted) return;
        this._state = "idle";
        resolve();
      }, totalMs);
      this.scheduledTimeouts.push(timeoutId);

      signal.addEventListener("abort", () => {
        window.clearTimeout(timeoutId);
        resolve();
      });
    });
  }

  playNote(midi: number, durationSec = 0.5): void {
    if (this._state === "uninitialized") return;
    void this.ensureRunning();
    const voice = this.voices[this.currentInstrument] ?? this.voices.synth!;
    voice.triggerAttackRelease(midiToNoteName(midi), durationSec, Tone.now() + 0.01);
    this.primedUntilMs = performance.now() + (durationSec + DEVICE_WARMDOWN_MS / 1000) * 1000;
  }

  stop(): void {
    if (this.currentPlaybackController) {
      this.currentPlaybackController.abort();
      this.currentPlaybackController = null;
    }
    for (const id of this.scheduledTimeouts) {
      window.clearTimeout(id);
    }
    this.scheduledTimeouts = [];
    this.latencyMonitor?.reset();
    this.latencyMonitor = null;
    for (const voice of Object.values(this.voices)) {
      if (voice && "releaseAll" in voice) {
        (voice as Tone.PolySynth).releaseAll();
      }
    }
    this._state = "stopped";
  }

  destroy(): void {
    this.stop();
    for (const voice of Object.values(this.voices)) {
      voice?.dispose();
    }
    this.masterVolume?.dispose();
    this.voices = {};
    this.masterVolume = null;
    this.primedUntilMs = 0;
    this._state = "uninitialized";
  }

  private scheduleEvent(
    voice: Voice,
    event: NoteEvent,
    baseTime: number,
    opts: AudioPlayerOptions,
  ): void {
    const noteName = midiToNoteName(event.midi);
    const velocity = gainToVelocity(event.gain);
    const startTime = baseTime + event.startTime;
    const duration = Math.max(0.05, event.duration);

    voice.triggerAttackRelease(noteName, duration, startTime, velocity);

    const startMs = (startTime - Tone.now()) * 1000;
    if (opts.onNoteStart || this.latencyMonitor) {
      const id = window.setTimeout(() => {
        this.latencyMonitor?.noteFired(performance.now());
        opts.onNoteStart?.({ midi: event.midi, voice: event.voice });
      }, Math.max(0, startMs));
      this.scheduledTimeouts.push(id);
    }
    if (opts.onNoteEnd) {
      const endMs = (startTime + duration - Tone.now()) * 1000;
      const id = window.setTimeout(() => {
        opts.onNoteEnd!({ midi: event.midi, voice: event.voice });
      }, Math.max(0, endMs));
      this.scheduledTimeouts.push(id);
    }
  }
}
